export interface PunchMethodOption {
  value: string;
  label: string;
}

export interface TimeClockPunchEmployee {
  id: number;
  name: string;
  registration: string;
  photo?: string | null;
}

export interface TimeClockPunchDevice {
  id: number;
  name: string;
  type: string;
}

export interface TimeClockPunch {
  id: number;
  nsr: number;
  employeeId: number;
  employee: TimeClockPunchEmployee | null;
  punchedAt: string;
  method: string;
  deviceId: number | null;
  device: TimeClockPunchDevice | null;
  faceId: string | null;
  similarity: number | null;
  latitude: number | null;
  longitude: number | null;
  recordHash: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdAt: string;
}

export interface PunchReceiptEmployee {
  name: string;
  registration: string;
  photo?: string | null;
}

export interface PunchReceiptCompany {
  name: string;
  cnpj?: string | null;
}

export interface PunchReceiptData {
  id: number;
  nsr: number;
  employeeId?: number;
  employee: PunchReceiptEmployee | null;
  punchedAt: string;
  recordHash: string;
  todayPunches?: { id: number; punchedAt: string }[];
  company?: PunchReceiptCompany | null;
}

export const punchReceiptName = (receipt: PunchReceiptData): string => {
  if (receipt.employee) return receipt.employee.name;
  return receipt.employeeId
    ? `Funcionário #${receipt.employeeId}`
    : "Funcionário";
};

export interface TimeClockPunchReceipt extends TimeClockPunch {
  todayPunches?: { id: number; punchedAt: string }[];
  company?: PunchReceiptCompany | null;
}

export interface SelfPunchEmployee {
  id: number;
  name: string;
  registration: string;
  photo: string | null;
  role: string | null;
}

export interface SelfPunchSheet {
  employee: SelfPunchEmployee;
  punches: TimeClockPunch[];
}

export const TIMECLOCK_ADMIN_ROLES = ["superadmin", "owner", "admin"];

export const timeClockPunchEmployeeLabel = (punch: TimeClockPunch): string =>
  punch.employee
    ? `${punch.employee.name} (${punch.employee.registration})`
    : `Funcionário #${punch.employeeId}`;

export const timeClockPunchDeviceLabel = (punch: TimeClockPunch): string => {
  if (punch.device) return punch.device.name;
  return punch.deviceId ? `Terminal removido (#${punch.deviceId})` : "—";
};

export const punchMethodLabel = (
  methods: PunchMethodOption[],
  value: string
): string => methods.find((method) => method.value === value)?.label ?? value;

export const formatSimilarity = (similarity: number): string =>
  `${similarity.toFixed(1)}%`;

export type PunchKind =
  | "entrada"
  | "saida_intervalo"
  | "retorno_intervalo"
  | "saida"
  | "outro";

export const PUNCH_KIND_LABEL: Record<PunchKind, string> = {
  entrada: "Entrada",
  saida_intervalo: "Saída para intervalo",
  retorno_intervalo: "Volta do intervalo",
  saida: "Saída",
  outro: "Marcação extra",
};

export const punchKindAt = (index: number, total: number): PunchKind => {
  if (index === 0) return "entrada";
  if (total === 2) return "saida";
  if (index === 1) return "saida_intervalo";
  if (index === 2) return "retorno_intervalo";
  return index === total - 1 ? "saida" : "outro";
};

export const punchKindsForDay = (count: number): PunchKind[] =>
  Array.from({ length: Math.max(0, count) }, (_, index) =>
    punchKindAt(index, count)
  );

export interface PunchDayEntry {
  id: number;
  punchedAt: string;
  kind: PunchKind;
}

export interface PunchDayGroup {
  key: string;
  date: Date;
  entries: PunchDayEntry[];
  workedMinutes: number;
  open: boolean;
}

const localDayKey = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const punchDayKey = (value: string, timeZone?: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  if (!timeZone) return localDayKey(date);

  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return localDayKey(date);
  }
};

const workedMinutesOf = (entries: PunchDayEntry[]): number => {
  let total = 0;

  for (let index = 0; index + 1 < entries.length; index += 2) {
    const start = new Date(entries[index].punchedAt).getTime();
    const end = new Date(entries[index + 1].punchedAt).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) continue;
    total += Math.round((end - start) / 60000);
  }

  return total;
};

export const groupPunchesByDay = (
  punches: { id: number; punchedAt: string }[],
  timeZone?: string
): PunchDayGroup[] => {
  const days = new Map<string, { id: number; punchedAt: string }[]>();

  punches.forEach((punch) => {
    const key = punchDayKey(punch.punchedAt, timeZone);
    if (!key) return;
    const bucket = days.get(key);
    if (bucket) bucket.push(punch);
    else days.set(key, [punch]);
  });

  return [...days.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, dayPunches]) => {
      const ordered = [...dayPunches].sort(
        (a, b) =>
          new Date(a.punchedAt).getTime() - new Date(b.punchedAt).getTime()
      );
      const kinds = punchKindsForDay(ordered.length);
      const entries = ordered.map((punch, index) => ({
        id: punch.id,
        punchedAt: punch.punchedAt,
        kind: kinds[index],
      }));

      return {
        key,
        date: new Date(entries[0].punchedAt),
        entries,
        workedMinutes: workedMinutesOf(entries),
        open: entries.length % 2 === 1,
      };
    });
};

export const formatWorkedMinutes = (minutes: number): string => {
  if (minutes <= 0) return "0min";

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}min`;
  if (!rest) return `${hours}h`;
  return `${hours}h ${String(rest).padStart(2, "0")}min`;
};

export interface TimeClockPunchFilterValues {
  employeeId?: number;
  deviceId?: number;
  method?: string;
  from?: string;
  to?: string;
}

const localDayInstant = (day: string, time: string): string =>
  new Date(`${day}T${time}`).toISOString();

export const appendTimeClockPunchFilters = (
  query: URLSearchParams,
  filters: TimeClockPunchFilterValues
): void => {
  if (filters.employeeId) query.set("employeeId", String(filters.employeeId));
  if (filters.deviceId) query.set("deviceId", String(filters.deviceId));
  if (filters.method) query.set("method", filters.method);
  if (filters.from)
    query.set("from", localDayInstant(filters.from, "00:00:00.000"));
  if (filters.to) query.set("to", localDayInstant(filters.to, "23:59:59.999"));
};

export const punchPeriodQuery = (from: string, to: string): string => {
  const query = new URLSearchParams();

  if (from) query.set("from", new Date(`${from}T00:00:00`).toISOString());
  if (to) query.set("to", new Date(`${to}T23:59:59.999`).toISOString());

  return query.toString();
};

export interface ManualPunchEmployee {
  id: number;
  name: string;
  registration: string;
}

export interface ManualPunchFormData {
  employee: ManualPunchEmployee | null;
  punchedAt: string;
}

export const emptyManualPunchForm = (): ManualPunchFormData => ({
  employee: null,
  punchedAt: "",
});

const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

export const validateManualPunchForm = (
  form: ManualPunchFormData
): string | null => {
  if (!form.employee) return "Selecione o funcionário.";

  if (form.punchedAt) {
    const value = new Date(form.punchedAt);
    if (Number.isNaN(value.getTime())) return "Momento da marcação inválido.";
    if (value.getTime() > Date.now() + FUTURE_TOLERANCE_MS)
      return "A marcação não pode ser registrada no futuro.";
  }

  return null;
};

export interface ManualPunchPayload {
  employeeId: number;
  punchedAt?: string;
}

export const toManualPunchPayload = (
  form: ManualPunchFormData
): ManualPunchPayload => {
  const payload: ManualPunchPayload = { employeeId: form.employee!.id };
  if (form.punchedAt)
    payload.punchedAt = new Date(form.punchedAt).toISOString();
  return payload;
};

export type ChainIssueType = "gap" | "hash";

export interface ChainIssue {
  type: ChainIssueType;
  nsr: number;
  punchId: number | null;
}

export interface ChainVerifyReport {
  valid: boolean;
  checked: number;
  fromNsr: number;
  lastCheckedNsr: number;
  lastNsr: number;
  nextNsr: number | null;
  totalIssues: number;
  issues: ChainIssue[];
}

export const CHAIN_ISSUE_LABEL: Record<ChainIssueType, string> = {
  gap: "Lacuna no NSR",
  hash: "Falha no encadeamento (hash)",
};
